import React, { Fragment, useEffect, useState, useRef } from "react";
import data from "../config/skillsData.json";
import "../styles/skills.scss";
import gsap from "gsap";


// preload a card image. resolves on error too, so a missing one can't stall the carousel
const preloadImg = (src?: string) => new Promise<void>((resolve) => {
    if (!src) return resolve();
    const img = new Image();
    img.onload = img.onerror = () => resolve();
    img.src = src;
});

// ⬡ stop 3: skills. a little carousel — GSAP slides the card, CSS grows the bars.
const Skills: React.FC = () => {

    // derived from the data so tab order and content order can't drift apart
    const skills = data.map((d) => d.key);

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isItemClicked, setIsItemClicked] = useState(false);
    const [barsReady, setBarsReady] = useState(false);
    // which `.fade-in` elements have scrolled into view (tracked by data-fade key)
    const [appeared, setAppeared] = useState<Set<string>>(new Set());

    const contentRef = useRef<HTMLDivElement>(null);   // GSAP animates this element
    const selectedIndexRef = useRef(selectedIndex);    // live index for the interval closure
    const isAnimating = useRef(false);                 // guards against overlapping transitions

    const selectedContent = data[selectedIndex];
    const selectedImg = selectedContent?.contentImg

    // live index for the interval closure
    useEffect(() => { selectedIndexRef.current = selectedIndex; }, [selectedIndex]);

    // warm every card image up front so a swap never waits on the network
    useEffect(() => { data.forEach((d) => preloadImg(d?.contentImg)); }, []);

    // reveal-on-scroll lives in state so the carousel's re-renders can't eat the class
    useEffect(() => {
        const io = new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const key = (entry.target as HTMLElement).dataset.fade;
                if (key) setAppeared((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
                obs.unobserve(entry.target);
            });
        }, { threshold: 0.15, rootMargin: "0px 0px -120px 0px" });
        document.querySelectorAll<HTMLElement>("[data-fade]").forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);

    // transitions don't run on mount: start the bars at 0, set real widths a frame later
    useEffect(() => {
        setBarsReady(false);
        let inner = 0;
        const outer = requestAnimationFrame(() => { inner = requestAnimationFrame(() => setBarsReady(true)); });
        return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); };
    }, [selectedIndex]);

    // slide out left, then swap (the swap kicks off the slide-in below)
    const goToIndex = (next: number) => {
        if (next === selectedIndexRef.current || isAnimating.current || !contentRef.current) return;
        isAnimating.current = true;
        // decode the next image while sliding out so it doesn't pop in mid-slide
        const imgReady = preloadImg(data[next]?.contentImg);
        gsap.to(contentRef.current, {
            xPercent: -100, opacity: 0, duration: 0.5, ease: "power2.in",
            onComplete: () => { imgReady.then(() => setSelectedIndex(next)); },
        });
    };

    // whenever the index changes, slide the new content IN from the right
    useEffect(() => {
        if (!contentRef.current) return;
        gsap.fromTo(contentRef.current,
            { xPercent: 100, opacity: 0 },
            { xPercent: 0, opacity: 1, duration: 0.6, ease: "power2.out",
                onComplete: () => { isAnimating.current = false; } });
    }, [selectedIndex]);

    // auto-advance, paused once someone clicks a skill
    useEffect(() => {
        if (isItemClicked) return;
        const interval = setInterval(() => {
            const cur = selectedIndexRef.current;
            goToIndex(cur === skills.length - 1 ? 0 : cur + 1);
        }, 11000);
        return () => clearInterval(interval);
    }, [isItemClicked, skills.length]);

    // ...and pick back up a while after the last click
    useEffect(() => {
        if (!isItemClicked) return;
        const resume = setTimeout(() => setIsItemClicked(false), 100000);
        return () => clearTimeout(resume);
    }, [isItemClicked]);

    const handleClick = (skillIndex: number) => {
        setIsItemClicked(true);
        goToIndex(skillIndex);
    };

    function textWithLineBreaks(prop: String) { // todo: combine this with barChart Animation to clean up return HTML script
        const textWithBreaks = prop.split('\n').map((text, index) => (
        <Fragment key={index}>
            {text}
            <br />
        </ Fragment>
        ))
        return <div className="bulletText">{textWithBreaks}</div>
    };

    function barChartAnimation(barText: String[], content: string) {
        const widths = content.split(',').map((w) => w.trim())

        const barChartTest = barText.map((text, index) => (
            <div className="skill-bar" key={index} >
                <div className="fill" style={{width: barsReady ? widths[index] : "0%"}} />
                <div className="skill-label">
                    <span className="skill-tag">{text}</span>
                    <span className="skill-pct">{widths[index]}</span>
                </div>
            </div>
        ))

        return <div className="flex bar-wrapper">{barChartTest}</div>
    };

    return (
        <Fragment>
            <div className="skills-body">
                <div className='content-width'>
                    <header
                        data-fade="__header"
                        className={`fade-in skills-header${appeared.has('__header') ? ' appear' : ''}`}
                    >
                        Skills
                    </header>
                    <div className='grid'>
                        {skills.map((skill, index) => (
                            <li
                                data-fade={skill}
                                className={`fade-in skills${appeared.has(skill) ? ' appear' : ''}${selectedContent?.key === skill ? ' activeSkill' : ''}`}
                                key={skill}
                                onClick={() => {handleClick(index);}}
                            >
                                {skill}
                            </li>
                        ))}
                    </div>
                    <div ref={contentRef} className="columns flex flex-row">
                        <div className="col content-container flex-50">
                            <header className="skills-header">{selectedContent?.key}</header>
                            <div className="skill-content">{selectedContent != null ? ( selectedContent.style === 0 ? (selectedContent.skills.join(" · ")) : "" ) : "" }
                            </div>
                            <br />
                            <div className="skill-body">{selectedContent != null ? ( selectedContent.style === 0 ? (textWithLineBreaks(selectedContent.content)) : (barChartAnimation(selectedContent.skills, selectedContent.content)) ) : "" }</div>
                        </div>
                        <div className="skill-image flex-50"><img src={selectedImg}  className="flex clipped" alt="" /></div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default Skills;
